import {
  identityBrowserContext,
  identityPopupContext,
  identityPopupRegistrationContinuationFromLocation,
  readBoundedJson,
} from "../account/kratos-browser-contract.js";
import { decodeActiveSession } from "../account/kratos-session-contract.js";
import {
  accountFlowMessageText,
  addAccountDisplayName,
  addAccountEmailAddressRow,
  createFlowView,
  createLoadingView,
  createLoginExpiredView,
  createSignedOutView,
  identityErrorPresentation,
  removeAccountDisplayName,
  removeAccountEmailAddressRow,
} from "../account/identity-account-view.js";
import { createAccountDeletionRepository } from "../account/account-deletion-repository.js";
import {
  browserAccountStateSessionRepository,
} from "../account/account-state-session-repository.js";
import {
  createAccountDeletionView,
  createAccountDeletionPendingView,
  createAccountDeletedView,
  createAccountSettingsView,
  updateAccountDeletionView,
} from "../account/identity-session-view.js";
import { element, translate } from "../account/identity-view-primitives.js";
import {
  accountBasePath,
  accountRouteForPathname,
  accountSettingsPanelUrls,
  currentAccountReturnPath,
} from "../account/account-route-contract.js";
import { createKratosBrowserClient } from "../account/kratos-browser-client.js";
import {
  browserKratosIdentitySessionRepository,
  refreshBrowserKratosIdentitySessionRepository,
} from "../account/kratos-identity-session-repository.js";
import {
  webauthnCeremonyFailureCopy,
  webauthnCeremonyAvailable,
  performWebAuthnCeremony,
} from "../account/webauthn-ceremony.js";
import {
  createPasskeyLabelProvider,
  exactPasskeyLabel,
} from "../account/passkey-label-provider.js";
import {
  identityProviderOutcomePresentation,
  isIdentityProviderSubmit,
  providerPopupSubmission,
  startIdentityProviderPopup,
} from "../account/identity-provider-popup.js";
import { identityFormSubmission } from "../account/identity-form-submission.js";
import {
  loadIdentitySessionManagement,
  revokeIdentitySessions,
} from "../account/identity-session-management.js";

export const ACCOUNT_SESSION_TAG = "fishy-account-session";

const HTMLElementBase = globalThis.HTMLElement ?? class {};
const FLOW_VIEWS_FOR_ACCOUNT = new Set([
  "login",
  "registration",
  "settings",
  "recovery",
  "verification",
]);

export function identitySubmitRequiresValidation(submitter) {
  return submitter?.formNoValidate !== true;
}

const SETTINGS_CREDENTIAL_MUTATIONS = Object.freeze([
  "passkey_settings_register",
  "passkey_remove",
  "webauthn_register",
  "webauthn_remove",
]);

function completedSettingsCredentialMutation(flow, submission, outcome) {
  return flow?.view === "settings"
    && outcome?.kind === "flow"
    && SETTINGS_CREDENTIAL_MUTATIONS.some((name) => Boolean(submission.get(name)));
}
let accountSessionInstance = 0;

export function initialIdentityFlowForSessionState(state) {
  if (state?.kind === "ready") return "settings";
  if (state?.kind === "signedOut") return "login";
  return null;
}

const IDENTITY_FLOW_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function identityFlowIdFromLocation(locationLike) {
  const entries = [...new URLSearchParams(String(locationLike?.search ?? "")).entries()];
  if (
    entries.length !== 1
    || entries[0][0] !== "flow"
    || !IDENTITY_FLOW_ID_PATTERN.test(entries[0][1])
  ) {
    return "";
  }
  return entries[0][1];
}

export function authenticatedIdentityFlowReturnUrl(flow, locationLike) {
  if (!identityFlowIdFromLocation(locationLike)) return "";
  if (
    flow?.view !== "login"
    || typeof flow.completionReturnUrl !== "string"
    || flow.completionReturnUrl.length === 0
  ) {
    throw new TypeError("Authenticated login flow has no validated return URL.");
  }
  return flow.completionReturnUrl;
}

export class FishyAccountSession extends HTMLElementBase {
  constructor() {
    super();
    this._connected = false;
    this._requestRevision = 0;
    this._abortController = null;
    this._sessionMutationController = null;
    this._flowMutationController = null;
    this._accountDeletionRepository = null;
    this._accountDeletionPending = false;
    this._accountDeletionIdentityChanged = false;
    this._passkeyController = null;
    this._passkeyLabelController = null;
    this._passkeyLabelProvider = null;
    this._passkeyLabels = Object.freeze({ kind: "missing", schemaVersion: 1 });
    this._selectedSettingsPanel = null;
    this._context = null;
    this._client = null;
    this._currentFlow = null;
    this._reauthFlow = null;
    this._reauthDialog = null;
    this._pendingSettingsMutation = null;
    this._verificationEmail = "";
    this._signedIn = null;
    this._identityRepository = null;
    this._identityUnsubscribe = null;
    this._active = false;
    this._loadStarted = false;
    this._sessionWarningKeys = new Set();
    this._discordImagesAllowed = false;
    accountSessionInstance += 1;
    this._fieldIdPrefix = `fishy-account-${accountSessionInstance}`;
    this._onClick = this._onClick.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onPopState = this._onPopState.bind(this);
  }

  connectedCallback() {
    this._connected = true;
    this.addEventListener("click", this._onClick);
    this.addEventListener("input", this._onInput);
    this.addEventListener("keydown", this._onKeyDown);
    this.addEventListener("submit", this._onSubmit);
    globalThis.window?.addEventListener("popstate", this._onPopState);
    this._active = accountRouteForPathname(
      globalThis.window?.location?.pathname,
    )?.area === "identity";
    if (this._active) this._ensureLoaded();
    else this.dataset.accountState = "inactive";
  }

  set discordImagesAllowed(value) {
    this._discordImagesAllowed = value === true;
    for (const avatar of this.querySelectorAll?.("fishy-account-avatar") ?? []) {
      avatar.externalImagesAllowed = this._discordImagesAllowed;
    }
    for (const picker of this.querySelectorAll?.("fishy-account-avatar-picker") ?? []) {
      picker.discordImagesAllowed = this._discordImagesAllowed;
    }
  }

  disconnectedCallback() {
    this._connected = false;
    this._requestRevision += 1;
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("input", this._onInput);
    this.removeEventListener("keydown", this._onKeyDown);
    this.removeEventListener("submit", this._onSubmit);
    globalThis.window?.removeEventListener("popstate", this._onPopState);
    this._identityUnsubscribe?.();
    this._identityUnsubscribe = null;
    this._abortController?.abort();
    this._abortController = null;
    this._sessionMutationController?.abort();
    this._sessionMutationController = null;
    this._flowMutationController?.abort();
    this._flowMutationController = null;
    this._disposeAccountDeletion();
    this._disposePasskey();
    this._reauthDialog?.remove();
    this._reauthDialog = null;
    this._reauthFlow = null;
    this._discardPendingSettingsMutation();
    this._verificationEmail = "";
  }

  set active(value) {
    const next = value === true;
    if (this._active === next) return;
    this._active = next;
    if (next && this._connected) this._ensureLoaded();
  }

  get active() {
    return this._active;
  }

  _ensureLoaded() {
    if (this._loadStarted) {
      this._syncRouteSelection();
      return;
    }
    this._loadStarted = true;
    this._identityRepository = browserKratosIdentitySessionRepository();
    this._passkeyLabelProvider = createPasskeyLabelProvider({
      apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
    });
    this._identityUnsubscribe = this._identityRepository.subscribe((state) => {
      if (
        this._connected
        && this._loadStarted
        && state.kind === "signedOut"
        && this.dataset.accountState === "settings"
      ) {
        void this._startFlow("login");
      }
    });
    void this._load();
  }

  _onPopState() {
    if (!this._connected || !this._active) return;
    this._syncRouteSelection();
  }

  _syncRouteSelection() {
    const route = accountRouteForPathname(globalThis.window?.location?.pathname);
    if (route?.area !== "identity") return;
    if (route.panel) this._selectSettingsPanel(route.panel);
  }

  _disposeAccountDeletion() {
    this._accountDeletionRepository?.dispose();
    this._accountDeletionRepository = null;
    this._accountDeletionPending = false;
  }

  _onInput(event) {
    const input = event.target?.closest?.("[data-account-delete-confirmation]");
    if (
      !input
      || !this.contains(input)
      || this._accountDeletionPending
    ) return;
    updateAccountDeletionView(this, "idle");
  }

  _onSubmit(event) {
    const form = event.target?.closest?.("form");
    if (!form || !this.contains(form)) return;
    if (form.matches("[data-account-delete-form]")) {
      event.preventDefault();
      const input = form.querySelector("[data-account-delete-confirmation]");
      if (
        this._accountDeletionPending
        || !this._accountDeletionRepository
        || !input
        || input.value !== input.dataset.accountDeleteConfirmation
      ) {
        return;
      }
      void this._deleteAccount();
      return;
    }
    if (!form.action.includes("/identity/self-service/")) return;
    event.preventDefault();
    void this._submitIdentityFlow(form, event.submitter);
  }

  _onKeyDown(event) {
    const tab = event.target?.closest?.("[data-account-access-tab]");
    if (!tab || !this.contains(tab)) return;
    const tabs = [...this.querySelectorAll("[data-account-access-tab]")];
    const current = tabs.indexOf(tab);
    if (current < 0) return;
    let next = null;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      next = (current + 1) % tabs.length;
    } else if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      next = (current - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = tabs.length - 1;
    }
    if (next === null) return;
    event.preventDefault();
    this._selectAccessMethod(tabs[next].dataset.accountAccessTab, {
      focus: true,
    });
  }

  _onClick(event) {
    const addDisplayName = event.target?.closest?.("[data-account-display-name-add]");
    if (addDisplayName && this.contains(addDisplayName)) {
      event.preventDefault();
      addAccountDisplayName(addDisplayName);
      return;
    }
    const removeDisplayName = event.target?.closest?.("[data-account-display-name-remove]");
    if (removeDisplayName && this.contains(removeDisplayName)) {
      event.preventDefault();
      removeAccountDisplayName(removeDisplayName);
      return;
    }
    const addEmail = event.target?.closest?.("[data-account-email-add]");
    if (addEmail && this.contains(addEmail)) {
      event.preventDefault();
      addAccountEmailAddressRow(addEmail);
      return;
    }
    const removeEmail = event.target?.closest?.("[data-account-email-remove]");
    if (removeEmail && this.contains(removeEmail)) {
      event.preventDefault();
      removeAccountEmailAddressRow(removeEmail);
      return;
    }
    const closeReauthentication = event.target?.closest?.(
      "[data-account-reauthentication-close]",
    );
    if (closeReauthentication && this.contains(closeReauthentication)) {
      event.preventDefault();
      this._closeReauthentication();
      return;
    }
    const accessTab = event.target?.closest?.("[data-account-access-tab]");
    if (accessTab && this.contains(accessTab)) {
      event.preventDefault();
      this._selectAccessMethod(accessTab.dataset.accountAccessTab);
      return;
    }
    const settingsTab = event.target?.closest?.("[data-account-settings-tab]");
    if (settingsTab && this.contains(settingsTab)) {
      event.preventDefault();
      const url = new URL(settingsTab.href);
      const route = accountRouteForPathname(url.pathname);
      if (url.origin === globalThis.window?.location?.origin && route?.area === "identity") {
        globalThis.window.history.pushState({}, "", url.pathname);
        this._selectSettingsPanel(route.panel);
      }
      return;
    }
    const startFlow = event.target?.closest?.("[data-account-start-flow]");
    if (startFlow && this.contains(startFlow)) {
      event.preventDefault();
      void this._startFlow(startFlow.dataset.accountStartFlow);
      return;
    }
    const verifyEmail = event.target?.closest?.("[data-account-verify-email]");
    if (verifyEmail && this.contains(verifyEmail)) {
      event.preventDefault();
      const selected = verifyEmail.dataset.accountVerifyEmail;
      if (!this._signedIn?.session.emailAddresses.some((address) => (
        address.value === selected && address.verification === "pending"
      ))) return;
      this._verificationEmail = selected;
      void this._startFlow("verification");
      return;
    }
    const startReauthentication = event.target?.closest?.(
      "[data-account-reauthentication-start]",
    );
    if (startReauthentication && this.contains(startReauthentication)) {
      event.preventDefault();
      void this._showReauthentication("refresh");
      return;
    }
    const signOut = event.target?.closest?.("[data-account-sign-out]");
    if (signOut && this.contains(signOut)) {
      event.preventDefault();
      void this._signOut();
      return;
    }
    const renamePasskey = event.target?.closest?.(
      "[data-account-passkey-label-rename]",
    );
    if (renamePasskey && this.contains(renamePasskey)) {
      event.preventDefault();
      void this._renamePasskey(renamePasskey);
      return;
    }
    const confirmation = event.target?.closest?.("[data-account-confirm]");
    if (
      confirmation
      && this.contains(confirmation)
      && !globalThis.window?.confirm?.(confirmation.dataset.accountConfirm)
    ) {
      event.preventDefault();
      return;
    }
    const webauthn = event.target?.closest?.("[data-ory-webauthn-trigger]");
    if (webauthn && this.contains(webauthn)) {
      event.preventDefault();
      if (!webauthn.disabled) {
        const trigger = webauthn.dataset.oryWebauthnTrigger;
        if (trigger === "oryPasskeyRegistration") {
          const form = webauthn.closest("form");
          if (!form?.reportValidity()) {
            return;
          }
        }
        void this._performWebAuthn(webauthn, trigger);
      }
      return;
    }
    const revokeSession = event.target?.closest?.("[data-account-session-revoke]");
    if (revokeSession && this.contains(revokeSession)) {
      event.preventDefault();
      void this._revokeOtherSessions(revokeSession.dataset.accountSessionRevoke);
      return;
    }
    const revokeAll = event.target?.closest?.("[data-account-sessions-revoke-all]");
    if (revokeAll && this.contains(revokeAll)) {
      event.preventDefault();
      void this._revokeOtherSessions();
      return;
    }
  }

  _selectAccessMethod(method, { focus = false } = {}) {
    const tabs = [...this.querySelectorAll("[data-account-access-tab]")];
    const panels = [...this.querySelectorAll("[data-account-access-panel]")];
    const selected = tabs.find((tab) => tab.dataset.accountAccessTab === method);
    if (!selected) return false;
    for (const tab of tabs) {
      const active = tab === selected;
      tab.classList.toggle("tab-active", active);
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of panels) {
      panel.hidden = panel.dataset.accountAccessPanel !== method;
    }
    if (focus) selected.focus();
    return true;
  }

  _selectSettingsPanel(id) {
    const tabs = [...this.querySelectorAll("[data-account-settings-tab]")];
    const panels = [...this.querySelectorAll("[data-account-settings-panel]")];
    const selected = tabs.find((tab) => tab.dataset.accountSettingsTab === id);
    if (!selected) return false;
    for (const tab of tabs) {
      const active = tab === selected;
      tab.classList.toggle("tab-active", active);
      if (active) tab.setAttribute("aria-current", "page");
      else tab.removeAttribute("aria-current");
    }
    for (const panel of panels) {
      panel.hidden = panel.dataset.accountSettingsPanel !== id;
    }
    this._selectedSettingsPanel = id;
    return true;
  }

  _initializeSettingsPanels() {
    const selectedExists = this._selectedSettingsPanel
      && this.querySelector(
        `[data-account-settings-tab="${this._selectedSettingsPanel}"]`,
      );
    const errorPanel = this.querySelector("[data-account-settings-has-error='true']");
    const firstTab = this.querySelector("[data-account-settings-tab]");
    const id = selectedExists
      ? this._selectedSettingsPanel
      : errorPanel?.dataset.accountSettingsPanel
        ?? firstTab?.dataset.accountSettingsTab;
    if (id) this._selectSettingsPanel(id);
  }

  _replace(content, state, { retainAccountDeletion = false } = {}) {
    this._disposePasskey();
    if (!retainAccountDeletion) this._disposeAccountDeletion();
    this.dataset.accountState = state;
    this.setAttribute("aria-busy", state === "loading" ? "true" : "false");
    this.replaceChildren(content);
  }

  _disposePasskey() {
    this._passkeyController?.abort("FishyStuff account passkey flow was disposed");
    this._passkeyController = null;
    this._passkeyLabelController?.abort("FishyStuff passkey name update was disposed");
    this._passkeyLabelController = null;
  }

  _projectPasskeyLabels() {
    for (const row of this.querySelectorAll("[data-account-passkey]")) {
      const credentialId = row.dataset.accountPasskeyCredential;
      const savedName = this._passkeyLabels.kind === "ready"
        ? String(this._passkeyLabels.labels?.[credentialId] ?? "")
        : "";
      const name = row.querySelector("[data-account-passkey-name]");
      row.dataset.accountPasskeyLabel = savedName;
      if (name) name.textContent = savedName || row.dataset.accountPasskeyDefaultName;
    }
  }

  async _renamePasskey(button) {
    if (this._passkeyLabelController || !this._passkeyLabelProvider) return;
    const row = button.closest("[data-account-passkey]");
    if (!row) return;
    const currentName = row.dataset.accountPasskeyLabel
      || row.dataset.accountPasskeyDefaultName;
    const label = globalThis.window?.prompt?.(
      translate(
        "account.passkey.rename_prompt",
        "Enter a new name for this passkey (1–80 characters).",
      ),
      currentName,
    );
    if (label === undefined || label === null) return;
    try {
      exactPasskeyLabel(label);
    } catch (_error) {
      globalThis.window?.__fishystuffToast?.error?.(
        translate(
          "account.passkey.name_invalid",
          "Use 1–80 characters without spaces at the beginning or end.",
        ),
        { duration: Infinity },
      );
      return;
    }

    const controller = new AbortController();
    this._passkeyLabelController = controller;
    row.setAttribute("aria-busy", "true");
    button.disabled = true;
    try {
      const session = await browserAccountStateSessionRepository().refresh();
      if (!this._connected || controller.signal.aborted) return;
      if (session.kind === "signedOut") {
        await this._startFlow("login");
        return;
      }
      if (session.kind !== "ready") {
        throw new Error("Account mutation authorization is unavailable.");
      }
      const result = await this._passkeyLabelProvider.setLabel(
        session,
        this._passkeyLabels,
        row.dataset.accountPasskeyCredential,
        label,
        { signal: controller.signal },
      );
      if (!this._connected || controller.signal.aborted) return;
      if (result.kind === "signedOut") {
        await this._startFlow("login");
        return;
      }
      this._passkeyLabels = result.authoritativeState;
      this._projectPasskeyLabels();
      if (result.kind === "conflict") {
        globalThis.window?.__fishystuffToast?.warning?.(
          translate(
            "account.passkey.name_conflict",
            "This passkey name changed elsewhere. The latest name is shown.",
          ),
          { duration: Infinity },
        );
        return;
      }
      globalThis.window?.__fishystuffToast?.success?.(
        translate("account.passkey.name_saved", "Passkey name saved."),
      );
      void refreshBrowserKratosIdentitySessionRepository();
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected) {
        globalThis.window?.__fishystuffToast?.error?.(
          translate("account.passkey.name_failed", "Passkey name could not be saved."),
          { duration: Infinity },
        );
      }
    } finally {
      if (this._passkeyLabelController === controller) {
        this._passkeyLabelController = null;
        if (row.isConnected) {
          row.removeAttribute("aria-busy");
          button.disabled = false;
        }
      }
    }
  }

  _setWebAuthnStatus(message, isError = false) {
    for (const status of this.querySelectorAll("[data-webauthn-status]")) {
      status.hidden = false;
      status.textContent = message;
      status.className = isError
        ? "text-sm text-error"
        : "text-sm text-base-content/60";
      status.setAttribute("role", isError ? "alert" : "status");
    }
  }

  async _performWebAuthn(button, trigger) {
    if (this._passkeyController) return;
    const form = button.closest("form");
    if (!form) return;
    const controller = new AbortController();
    this._passkeyController = controller;
    button.disabled = true;
    try {
      await performWebAuthnCeremony(form, trigger, { signal: controller.signal });
      if (!controller.signal.aborted && this._connected) form.requestSubmit();
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected) {
        const copy = webauthnCeremonyFailureCopy(error, trigger);
        this._setWebAuthnStatus(
          translate(copy.key, copy.fallback),
          true,
        );
      }
    } finally {
      if (this._passkeyController === controller) {
        this._passkeyController = null;
        if (button.isConnected) button.disabled = button.dataset.oryDisabled === "true";
      }
    }
  }

  _activateWebAuthn(scriptContract) {
    if (!scriptContract) return;
    const available = webauthnCeremonyAvailable();
    for (const button of this.querySelectorAll("[data-ory-webauthn-trigger]")) {
      button.disabled = !available || button.dataset.oryDisabled === "true";
    }
    for (const status of this.querySelectorAll("[data-webauthn-status]")) {
      if (available) {
        status.textContent = "";
        status.hidden = true;
      } else {
        const method = status.closest("[data-account-settings-method]")
          ?.dataset.accountSettingsMethod;
        status.hidden = false;
        status.textContent = method === "webauthn"
          ? translate("account.webauthn.unavailable", "Security-key support is unavailable.")
          : translate("account.passkey.unavailable", "Passkey support is unavailable.");
        status.className = "text-sm text-error";
        status.setAttribute("role", "alert");
      }
    }
  }

  _renderLoading() {
    this._replace(createLoadingView(), "loading");
  }

  _renderSignedOut(context) {
    this._accountDeletionIdentityChanged = false;
    this._replace(createSignedOutView(context), "signedOut");
  }

  _renderFlow(flow, context, options = {}) {
    this._selectedSettingsPanel = null;
    this._currentFlow = flow;
    this._signedIn = null;
    this._replace(
      createFlowView(flow, context, this._fieldIdPrefix, {
        ...options,
        knownVerificationEmail: flow.view === "verification"
          ? this._verificationEmail
          : "",
      }),
      flow.view,
    );
    this._activateWebAuthn(flow.webauthnScript);
  }

  _renderSettings(flow, context, signedIn) {
    this._currentFlow = flow;
    this._signedIn = signedIn;
    this._passkeyLabels = signedIn.session.passkeyLabels;
    const identityChanged = this._accountDeletionIdentityChanged;
    this._accountDeletionIdentityChanged = false;
    this._disposeAccountDeletion();
    this._accountDeletionRepository = createAccountDeletionRepository({
      apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
      verifyCurrentIdentity: (signal) => (
        this._verifyAccountDeletionIdentity(signedIn.session, context, signal)
      ),
    });
    const settingsView = createFlowView(
      flow,
      context,
      `${this._fieldIdPrefix}-settings`,
      {
        discordAvatarUrl: signedIn.session.discordAvatarUrl,
        discordImagesAllowed: this._discordImagesAllowed,
        emailAddresses: signedIn.session.emailAddresses,
        passkeyLabels: this._passkeyLabels,
      },
    );
    const returnPathname = new URL(context.returnUrl).pathname;
    if (accountRouteForPathname(globalThis.window?.location?.pathname)?.startFlow) {
      globalThis.window.history.replaceState(
        globalThis.window.history.state,
        "",
        accountBasePath(globalThis.window.location),
      );
    }
    this._selectedSettingsPanel = accountRouteForPathname(
      globalThis.window?.location?.pathname,
    )?.panel ?? "profile";
    this._replace(
      createAccountSettingsView(
        settingsView,
        signedIn.logoutUrl,
        signedIn.otherSessions,
        createAccountDeletionView(`@${signedIn.session.traits.username}`),
        accountSettingsPanelUrls(context.pageOrigin, returnPathname),
      ),
      "settings",
      { retainAccountDeletion: true },
    );
    if (
      globalThis.window?.location?.search
      && typeof globalThis.window?.history?.replaceState === "function"
    ) {
      globalThis.window.history.replaceState(
        globalThis.window.history.state,
        "",
        globalThis.window.location.pathname,
      );
    }
    this._initializeSettingsPanels();
    if (identityChanged) {
      updateAccountDeletionView(this, "identityChanged", context);
    }
    for (const message of flow.messages) {
      const tone = message.type === "error"
        ? "error"
        : message.type === "success" ? "success" : "info";
      globalThis.window?.__fishystuffToast?.[tone]?.(
        accountFlowMessageText(message),
      );
    }
    this._activateWebAuthn(flow.webauthnScript);
    this._showSessionWarnings(signedIn.session);
  }

  _showSessionWarnings(session) {
    for (const warning of session?.warnings ?? []) {
      const key = `${warning.kind}:${warning.message}`;
      if (this._sessionWarningKeys.has(key)) continue;
      this._sessionWarningKeys.add(key);
      globalThis.window?.__fishystuffToast?.warning?.(warning.message, {
        duration: Infinity,
      });
    }
  }

  _identityContext() {
    const location = globalThis.window?.location;
    const continuation = identityPopupRegistrationContinuationFromLocation(location);
    const contextOptions = {
      apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
      identityBaseUrl: globalThis.window?.__fishystuffRuntimeConfig?.identity?.baseUrl,
      locationLike: location,
      passkeyRpId: globalThis.window?.__fishystuffRuntimeConfig?.identity?.passkeyRpId,
    };
    let context;
    if (continuation) {
      context = identityPopupContext({
        ...contextOptions,
        operationId: continuation.operationId,
      });
    } else {
      const route = accountRouteForPathname(location?.pathname);
      const returnPath = route?.returnPath ?? currentAccountReturnPath(location);
      const loginReturnPath = identityFlowIdFromLocation(location)
        ? returnPath
        : `${location?.pathname ?? "/"}${location?.search ?? ""}${location?.hash ?? ""}`;
      context = identityBrowserContext({
        ...contextOptions,
        returnPath,
        loginReturnPath,
      });
    }
    this._context = context;
    this._client = createKratosBrowserClient({ context });
    return context;
  }

  _setFormPending(form, pending) {
    form?.toggleAttribute?.("aria-busy", pending);
    for (const control of form?.querySelectorAll?.("button, input, select, textarea") ?? []) {
      if (pending) {
        control.dataset.accountPendingDisabled = String(control.disabled);
        control.disabled = true;
      } else if (Object.hasOwn(control.dataset, "accountPendingDisabled")) {
        control.disabled = control.dataset.accountPendingDisabled === "true";
        delete control.dataset.accountPendingDisabled;
      }
    }
  }

  _flowForForm(form) {
    if (this._reauthDialog?.contains(form)) return this._reauthFlow;
    return this._currentFlow;
  }

  _returnFromDedicatedAal2() {
    const route = accountRouteForPathname(globalThis.window?.location?.pathname);
    if (route?.startFlow !== "aal2" || !route.returnPath || !this._context) return false;
    globalThis.window.location.assign(this._context.returnUrl);
    return true;
  }

  async _renderSettingsOutcome(flow, context, signal, { refreshIdentity = false } = {}) {
    const identityState = refreshIdentity
      ? await refreshBrowserKratosIdentitySessionRepository()
      : await this._identityRepository.load();
    if (identityState.kind === "signedOut") {
      await this._startFlow("login");
      return;
    }
    if (identityState.kind !== "ready") {
      throw new Error("Identity session is unavailable.");
    }
    const signedIn = await loadIdentitySessionManagement({
      client: this._client ?? createKratosBrowserClient({ context }),
      context,
      signal,
      session: identityState.session,
    });
    if (signedIn.kind === "expired") {
      await this._startFlow("login");
      return;
    }
    if (signedIn.kind === "reauthenticationRequired") {
      await this._showReauthentication(signedIn.mode);
      return;
    }
    this._renderSettings(flow, context, signedIn);
  }

  async _startFlow(view, { mode = "default", preserve = false, flowId = "" } = {}) {
    if (!FLOW_VIEWS_FOR_ACCOUNT.has(view)) return;
    if (view !== "verification") this._verificationEmail = "";
    this._requestRevision += 1;
    const revision = this._requestRevision;
    this._abortController?.abort();
    const controller = new AbortController();
    this._abortController = controller;
    const context = this._identityContext();
    if (!preserve) this._renderLoading();
    try {
      const outcome = flowId
        ? await this._client.loadFlow(view, flowId, { signal: controller.signal })
        : await this._client.startFlow(view, {
          mode,
          signal: controller.signal,
        });
      if (!this._connected || revision !== this._requestRevision) return;
      if (outcome.kind === "sessionAlreadyAvailable") {
        const identity = await refreshBrowserKratosIdentitySessionRepository();
        if (!this._connected || revision !== this._requestRevision) return;
        if (identity.kind !== "ready") throw new Error("Identity session could not be loaded.");
        await this._startFlow("settings");
        return;
      }
      if (outcome.kind === "signedOut" && view === "settings") {
        await this._startFlow("login");
        return;
      }
      if (outcome.kind === "reauthenticationRequired") {
        await this._showReauthentication(outcome.mode);
        return;
      }
      if (outcome.kind !== "flow") {
        throw new Error(`Identity ${view} did not return a browser flow.`);
      }
      if (view === "settings") {
        await this._renderSettingsOutcome(outcome.flow, context, controller.signal);
      } else {
        this._renderFlow(outcome.flow, context);
      }
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected && revision === this._requestRevision) {
        this._renderError(error, context);
      }
    } finally {
      if (this._abortController === controller) this._abortController = null;
    }
  }

  _closeReauthentication({ preservePendingSettingsMutation = false } = {}) {
    this._reauthDialog?.close?.();
    this._reauthDialog?.remove();
    this._reauthDialog = null;
    this._reauthFlow = null;
    if (!preservePendingSettingsMutation) {
      this._discardPendingSettingsMutation();
    }
  }

  _discardPendingSettingsMutation() {
    this._pendingSettingsMutation?.providerContinuation?.cancel?.();
    this._pendingSettingsMutation = null;
  }

  _retainSettingsMutation(flow, submission) {
    if (flow?.view !== "settings" || !(submission instanceof URLSearchParams)) {
      throw new TypeError("Only an exact settings submission can await reauthentication.");
    }
    this._pendingSettingsMutation = Object.freeze({
      kind: "native",
      flow,
      submission: new URLSearchParams(submission),
      selectedPanel: this._selectedSettingsPanel,
    });
  }

  _retainDiscordSettingsMutation(flow, submission, providerContinuation) {
    if (
      flow?.view !== "settings"
      || !(submission instanceof URLSearchParams)
      || providerContinuation?.kind !== "reauthenticationRequired"
      || typeof providerContinuation.resume !== "function"
      || typeof providerContinuation.cancel !== "function"
    ) {
      throw new TypeError("Only a resumable Discord settings submission can await reauthentication.");
    }
    this._pendingSettingsMutation = Object.freeze({
      kind: "discord",
      flow,
      providerContinuation,
      submission: new URLSearchParams(submission),
      selectedPanel: this._selectedSettingsPanel,
    });
  }

  async _resumeSettingsMutation(signal) {
    const pending = this._pendingSettingsMutation;
    this._pendingSettingsMutation = null;
    if (!pending) return "none";
    if (pending.selectedPanel) this._selectedSettingsPanel = pending.selectedPanel;

    if (pending.kind === "discord") {
      const outcome = await pending.providerContinuation.resume({ signal });
      await this._completeProviderPopup(
        outcome,
        pending.flow,
        signal,
        { sourceSubmission: pending.submission },
      );
      return "provider";
    }

    const outcome = await this._client.submitFlow(
      pending.flow,
      new URLSearchParams(pending.submission),
      { signal },
    );
    if (outcome.kind === "expired") {
      globalThis.window?.__fishystuffToast?.warning?.(
        translate(
          "account.error.flow_expired",
          "That security form expired. Review the fresh form and submit it again.",
        ),
        { duration: Infinity },
      );
      await this._startFlow("settings");
      return "expired";
    }
    if (outcome.kind === "signedOut") {
      await this._startFlow("login");
      return "signedOut";
    }
    if (!["flow", "validation"].includes(outcome.kind) || outcome.flow.view !== "settings") {
      throw new Error("The account change could not continue after login renewal.");
    }
    await this._renderSettingsOutcome(
      outcome.flow,
      this._context,
      signal,
      { refreshIdentity: outcome.kind === "flow" },
    );
    return outcome.kind;
  }

  async _finishReauthentication(sourceFlow, signal, identityState = null) {
    if (sourceFlow !== this._reauthFlow) return false;
    this._closeReauthentication({ preservePendingSettingsMutation: true });
    const identity = identityState
      ?? await refreshBrowserKratosIdentitySessionRepository();
    if (identity.kind !== "ready") {
      this._discardPendingSettingsMutation();
      throw new Error("Identity session is unavailable after login renewal.");
    }
    const resumed = await this._resumeSettingsMutation(signal);
    if (resumed === "none") await this._startFlow("settings");
    if (resumed === "flow") {
      globalThis.window?.__fishystuffToast?.success?.(
        translate(
          "account.reauthentication.complete",
          "Login renewed and your change was completed.",
        ),
      );
    }
    return true;
  }

  async _showReauthentication(mode = "refresh") {
    if (this._reauthDialog) return;
    const context = this._context ?? this._identityContext();
    const outcome = await this._client.startFlow("login", { mode });
    if (outcome.kind !== "flow") {
      throw new Error("Identity reauthentication could not be initialized.");
    }
    this._reauthFlow = outcome.flow;
    const dialog = element("dialog", {
      className: "modal modal-open",
    });
    dialog.dataset.accountReauthentication = "";
    const box = element("div", {
      className: "modal-box max-w-3xl",
    });
    box.append(createFlowView(
      outcome.flow,
      context,
      `${this._fieldIdPrefix}-reauthentication`,
    ));
    const actions = element("div", { className: "modal-action" });
    const close = element("button", {
      className: "btn btn-ghost",
      text: translate("account.action.cancel", "Cancel"),
    });
    close.type = "button";
    close.dataset.accountReauthenticationClose = "";
    actions.append(close);
    box.append(actions);
    dialog.append(box);
    this.append(dialog);
    this._reauthDialog = dialog;
    dialog.showModal?.();
    this._activateWebAuthn(outcome.flow.webauthnScript);
  }

  async _submitIdentityFlow(form, submitter) {
    if (
      this._flowMutationController
      || (
        identitySubmitRequiresValidation(submitter)
        && typeof form.reportValidity === "function"
        && !form.reportValidity()
      )
    ) return;
    const flow = this._flowForForm(form);
    if (!flow || !this._client) return;
    const submission = identityFormSubmission(form, submitter);
    const controller = new AbortController();
    this._flowMutationController = controller;
    this._setFormPending(form, true);
    try {
      const providerPopup = isIdentityProviderSubmit(flow, submitter);
      const providerContinuation = providerPopup
        && flow.registrationEntry === "discordLoginFallback";
      const flowMode = flow.refresh
        ? "refresh"
        : flow.requestedAal === "aal2" ? "aal2" : "default";
      const outcome = providerContinuation
        ? await this._client.startProvider(
            flow,
            providerPopupSubmission(flow, submission),
            { signal: controller.signal },
          )
        : providerPopup
          ? await startIdentityProviderPopup({
              view: flow.view,
              mode: flowMode,
              sourceSubmission: submission,
              apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
              locationLike: globalThis.window?.location,
              signal: controller.signal,
            })
          : await this._client.submitFlow(flow, submission, {
              signal: controller.signal,
            });
      if (providerContinuation) {
        if (outcome.kind !== "providerNavigation") {
          throw new Error("Discord registration continuation did not navigate.");
        }
        globalThis.window?.location?.replace?.(outcome.redirectUrl);
        return;
      }
      if (!this._connected || controller.signal.aborted) return;
      if (providerPopup) {
        await this._completeProviderPopup(
          outcome,
          flow,
          controller.signal,
          { sourceSubmission: submission },
        );
        return;
      }
      if (outcome.kind === "providerNavigation") {
        throw new Error("Identity provider navigation escaped its popup owner.");
      }
      if (outcome.kind === "identityContinuation") {
        if (flow.view === "settings") {
          this._retainSettingsMutation(flow, submission);
        }
        await this._showReauthentication(outcome.mode);
        return;
      }
      if (outcome.kind === "expired") {
        globalThis.window?.__fishystuffToast?.warning?.(
          translate(
            "account.error.flow_expired",
            "That security form expired. Review the fresh form and submit it again.",
          ),
          { duration: Infinity },
        );
        await this._startFlow(flow.view, {
          mode: flow.refresh ? "refresh" : flow.requestedAal === "aal2" ? "aal2" : "default",
        });
        return;
      }
      if (outcome.kind === "signedOut") {
        await this._startFlow("login");
        return;
      }
      if (outcome.kind === "reauthenticationRequired") {
        if (flow.view === "settings") {
          this._retainSettingsMutation(flow, submission);
        }
        await this._showReauthentication(outcome.mode);
        return;
      }
      if (outcome.kind === "authenticated") {
        const returnUrl = authenticatedIdentityFlowReturnUrl(
          flow,
          globalThis.window?.location,
        );
        if (returnUrl) {
          globalThis.window.location.assign(returnUrl);
          return;
        }
        if (await this._finishReauthentication(flow, controller.signal)) return;
        if (this._returnFromDedicatedAal2()) return;
        this._closeReauthentication();
        await refreshBrowserKratosIdentitySessionRepository();
        await this._startFlow("settings");
        return;
      }
      if (["flow", "validation"].includes(outcome.kind)) {
        if (completedSettingsCredentialMutation(flow, submission, outcome)) {
          await this._startFlow("settings", { preserve: true });
          return;
        }
        if (outcome.flow.view === "settings") {
          await this._renderSettingsOutcome(
            outcome.flow,
            this._context,
            controller.signal,
            { refreshIdentity: outcome.kind === "flow" },
          );
        } else {
          this._renderFlow(outcome.flow, this._context);
        }
      }
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected) {
        if (!this._reauthDialog) this._discardPendingSettingsMutation();
        const presentation = identityErrorPresentation(error);
        globalThis.window?.__fishystuffToast?.error?.(
          presentation.detail,
          { title: presentation.title, duration: Infinity },
        );
      }
    } finally {
      if (this._flowMutationController === controller) {
        this._flowMutationController = null;
        if (form.isConnected) this._setFormPending(form, false);
      }
    }
  }

  async _completeProviderPopup(
    outcome,
    sourceFlow,
    signal,
    { sourceSubmission = new URLSearchParams() } = {},
  ) {
    if (outcome.kind === "reauthenticationRequired") {
      if (sourceFlow.view === "settings") {
        this._retainDiscordSettingsMutation(sourceFlow, sourceSubmission, outcome);
      }
      await this._showReauthentication(outcome.mode);
      return;
    }
    if (outcome.kind !== "returned") {
      const copy = identityProviderOutcomePresentation(outcome.kind);
      globalThis.window?.__fishystuffToast?.error?.(
        translate(copy.key, copy.fallback),
        { duration: Infinity },
      );
      return;
    }
    const identity = await refreshBrowserKratosIdentitySessionRepository();
    if (identity.kind !== "ready") {
      const copy = identityProviderOutcomePresentation("sessionUnavailable");
      globalThis.window?.__fishystuffToast?.error?.(
        translate(copy.key, copy.fallback),
        { duration: Infinity },
      );
      return;
    }
    const returnUrl = authenticatedIdentityFlowReturnUrl(
      sourceFlow,
      globalThis.window?.location,
    );
    if (returnUrl) {
      globalThis.window.location.assign(returnUrl);
      return;
    }
    if (await this._finishReauthentication(sourceFlow, signal, identity)) return;
    if (this._returnFromDedicatedAal2()) return;
    await this._startFlow("settings");
    if (sourceFlow.view === "settings") {
      globalThis.window?.__fishystuffToast?.success?.(
        translate("account.discord_linked", "Discord is now linked to your account."),
      );
    }
  }

  async _signOut() {
    if (this._flowMutationController || !this._client) return;
    const controller = new AbortController();
    this._flowMutationController = controller;
    try {
      const logout = await this._client.createLogout({ signal: controller.signal });
      if (logout.kind === "ready") {
        await this._client.logout(logout.logoutUrl, { signal: controller.signal });
      }
      await refreshBrowserKratosIdentitySessionRepository();
      if (this._connected && !controller.signal.aborted) await this._startFlow("login");
    } catch (error) {
      if (error?.name !== "AbortError") {
        globalThis.window?.__fishystuffToast?.error?.(
          translate("account.error.sign_out", "Sign out could not be completed."),
          { duration: Infinity },
        );
      }
    } finally {
      if (this._flowMutationController === controller) {
        this._flowMutationController = null;
      }
    }
  }

  async _deleteAccount() {
    const repository = this._accountDeletionRepository;
    if (!repository || this._accountDeletionPending) return;
    const context = this._currentIdentityContext();
    this._accountDeletionPending = true;
    updateAccountDeletionView(this, "pending", context);
    const deletion = await repository.deleteAccount();
    if (!this._connected || this._accountDeletionRepository !== repository) return;
    this._accountDeletionPending = false;
    if (deletion.kind === "deleted") {
      this._replace(createAccountDeletedView(), "deleted");
      this.querySelector("[data-account-deleted]")
        ?.focus();
      return;
    }
    if (deletion.kind === "deletionPending") {
      this._replace(createAccountDeletionPendingView(), "deletionPending");
      this.querySelector("[data-account-deletion-pending]")
        ?.focus();
      return;
    }
    if (deletion.kind === "signedOut") {
      this._renderLoginExpired(context);
      return;
    }
    if (deletion.kind === "reauthenticationRequired") {
      updateAccountDeletionView(
        this,
        "reauthenticationRequired",
        context,
      );
      return;
    }
    if (deletion.kind === "identityRefreshRequired") {
      this._accountDeletionIdentityChanged = true;
      await this._load();
      return;
    }
    if (deletion.kind === "retryable") {
      updateAccountDeletionView(this, "retryable", context);
      return;
    }
    updateAccountDeletionView(this, "idle", context);
  }

  async _verifyAccountDeletionIdentity(expectedSession, context, signal) {
    const response = await this._fetchJson(context.sessionUrl, signal);
    if (response.status === 401) return "signedOut";
    if (!response.ok) return "retryable";
    const currentSession = decodeActiveSession(
      await readBoundedJson(response, "Identity session"),
    );
    return currentSession.id === expectedSession.id
      && currentSession.traits.username === expectedSession.traits.username
      ? "current"
      : "changed";
  }

  _renderIdentityError(error, context) {
    const presentation = identityErrorPresentation(error);
    this._replace(element("div"), "identityError");
    globalThis.window?.__fishystuffToast?.error?.(
      presentation.detail,
      {
        title: presentation.title,
        duration: Infinity,
      },
    );
  }

  _renderLoginExpired(context) {
    this._replace(createLoginExpiredView(context), "loginExpired");
  }

  _renderError(error, context) {
    this._renderIdentityError(error, context);
  }

  async _fetchJson(url, signal) {
    return fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
  }

  _sessionMutationStatus(message, { error = false } = {}) {
    const status = this.querySelector("[data-account-session-mutation-status]");
    if (!status) {
      return;
    }
    status.textContent = message;
    status.className = error ? "text-sm text-error" : "text-sm text-base-content/65";
    status.setAttribute("role", error ? "alert" : "status");
  }

  _setSessionMutationDisabled(disabled) {
    for (const button of this.querySelectorAll(
      "[data-account-session-revoke], [data-account-sessions-revoke-all]",
    )) {
      button.disabled = disabled;
    }
  }

  _currentIdentityContext() {
    return identityBrowserContext({
      apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
      identityBaseUrl: globalThis.window?.__fishystuffRuntimeConfig?.identity?.baseUrl,
      locationLike: window.location,
      passkeyRpId: globalThis.window?.__fishystuffRuntimeConfig?.identity?.passkeyRpId,
      returnPath: currentAccountReturnPath(window.location),
    });
  }

  async _revokeOtherSessions(sessionId = "") {
    if (this._sessionMutationController) {
      return;
    }
    const context = this._currentIdentityContext();
    const controller = new AbortController();
    this._sessionMutationController = controller;
    this._setSessionMutationDisabled(true);
    this._sessionMutationStatus(sessionId
      ? translate("account.sessions.revoking", "Signing out the selected session…")
      : translate("account.sessions.revoking_all", "Signing out all other sessions…"));
    try {
      const outcome = await revokeIdentitySessions({
        context,
        sessionId,
        signal: controller.signal,
      });
      if (outcome.kind === "signedOut") {
        this._renderLoginExpired(context);
        return;
      }
      if (outcome.kind === "reauthenticationRequired") {
        await this._showReauthentication(outcome.mode);
        return;
      }
      if (this._connected && !controller.signal.aborted) {
        await this._load();
      }
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected) {
        this._sessionMutationStatus(
          translate(
            "account.sessions.revoke_failed",
            "That device could not be signed out. Try again.",
          ),
          { error: true },
        );
      }
    } finally {
      if (this._sessionMutationController === controller) {
        this._sessionMutationController = null;
        this._setSessionMutationDisabled(false);
      }
    }
  }

  async _load() {
    this._requestRevision += 1;
    const requestRevision = this._requestRevision;
    this._abortController?.abort();
    const abortController = new AbortController();
    this._abortController = abortController;
    this._renderLoading();

    let context = null;
    try {
      context = this._identityContext();
      const pathnameRoute = accountRouteForPathname(window.location.pathname);
      const providerRegistration =
        identityPopupRegistrationContinuationFromLocation(window.location);
      if (providerRegistration && pathnameRoute?.startFlow !== "registration") {
        throw new TypeError("Discord registration continuation is outside registration.");
      }
      const flowId = providerRegistration?.flowId
        ?? identityFlowIdFromLocation(window.location);
      if (flowId) {
        let view = pathnameRoute?.startFlow && pathnameRoute.startFlow !== "aal2"
          ? pathnameRoute.startFlow
          : "";
        if (!view) {
          const session = await this._client.session({ signal: abortController.signal });
          view = session.kind === "ready" ? "settings" : "login";
        }
        await this._startFlow(view, { flowId });
        if (providerRegistration) {
          if (this._currentFlow?.registrationEntry !== "discordLoginFallback") {
            throw new TypeError("Discord registration continuation flow is invalid.");
          }
          globalThis.window?.history?.replaceState?.(
            globalThis.window.history.state,
            "",
            globalThis.window.location.pathname,
          );
        }
        return;
      }
      const identityState = await this._identityRepository.load();
      if (pathnameRoute?.startFlow) {
        if (pathnameRoute.startFlow === "aal2") {
          await this._startFlow("login", { mode: "aal2" });
          return;
        }
        await this._startFlow(
          identityState.kind === "ready" ? "settings" : pathnameRoute.startFlow,
        );
        return;
      }
      if (!this._connected || requestRevision !== this._requestRevision) return;
      const initialFlow = initialIdentityFlowForSessionState(identityState);
      if (initialFlow) return this._startFlow(initialFlow);
      throw new Error("Account session is unavailable.");
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      if (this._connected && requestRevision === this._requestRevision) {
        this._renderError(error, context);
      }
    } finally {
      if (requestRevision === this._requestRevision) {
        this._abortController = null;
      }
    }
  }
}

export function registerAccountSession() {
  if (
    typeof globalThis.customElements === "undefined"
    || globalThis.customElements.get(ACCOUNT_SESSION_TAG)
  ) {
    return;
  }
  globalThis.customElements.define(ACCOUNT_SESSION_TAG, FishyAccountSession);
}

registerAccountSession();
